import Head from 'next/head'
import { useRouter } from 'next/router'
import { Fragment, useEffect } from 'react'

import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'

import { trackEvent } from '@/lib/api/metrics'

import { useEmailOverlay } from '@/hooks/auth/useEmailOverlay'
import useLocations from '@/hooks/marketplace/useLocations'
import { useShopPage } from '@/hooks/marketplace/useShopPage'
import useAuthStore from '@/hooks/stores/useAuthStore'
import {
  DEFAULT_LOCATION_ID,
  DEFAULT_PREORDER,
  useDeviceFilterStore
} from '@/hooks/stores/useDeviceFilterStore'

import InfiniteScroll from '@/components/InfiniteScroll'
import InfoMessage from '@/components/InfoMessage'
import Loader from '@/components/Loader'
import SelectBox from '@/components/SelectBox'
import { MultiEmailsDialog } from '@/components/dialogs/auth/MultiEmailsDialog'
import ShopCard from '@/components/features/shop/ShopCard'
import ShopEmptyBoard from '@/components/features/shop/ShopEmptyBoard'
import { ShopPriceFilterModal } from '@/components/features/shop/ShopPriceFilterModal'
import SortSelectBox from '@/components/selectbox/SortSelectBox'
import ShopCardSkeleton from '@/components/skeletons/ShopCardSkeleton'

const ShopPage = () => {
  const { formatMessage } = useIntl()
  const { locations } = useLocations()
  const {
    needsEmailAddition,
    onAddEmailDialogOpen,
    onAddEmailDialogClose,
    needsEmailConfirmation
  } = useEmailOverlay()
  const { locationId, preorder, setLocationId, setPreorder } =
    useDeviceFilterStore()
  const router = useRouter()
  const { accessToken } = useAuthStore()

  const {
    data,
    form,
    isFetching,
    paymentFailed,
    isFetchingNextPage,
    sortParams,
    SORT_OPTIONS,
    AVAILABILITY_OPTIONS,
    fetchNextPage,
    maxPrice,
    maxPricePerTH,
    minPrice,
    minPricePerTH,
    areSamePrices
  } = useShopPage()

  const modifiedLocations = [
    {
      value: null,
      title: formatMessage({ id: 'location.any', defaultMessage: 'Любое' })
    },
    ...(locations || []).map((location) => {
      return {
        value: location.id,
        title: location.name
      }
    })
  ]

  useEffect(() => {
    if (router.isReady && accessToken) {
      trackEvent('shop_registered')
    }
  }, [router.isReady, accessToken])

  return (
    <div className="pb-10 pt-3 tablet:pt-4 desktop:pb-6 desktop:pt-5">
      <Head>
        <title>
          {`${formatMessage({
            id: 'page.shop',
            defaultMessage: 'Магазин'
          })} — Asterbit`}
        </title>
      </Head>
      <h1 className="mb-4 text-2xl font-medium leading-xs tablet:mb-5 tablet:text-3xl tablet:leading-3xl desktop:mb-6">
        <FormattedMessage {...{ id: 'page.shop', defaultMessage: 'Магазин' }} />
      </h1>
      {paymentFailed && (
        <InfoMessage
          className="mb-4"
          {...{
            type: 'error',
            title: formatMessage({
              id: 'payment.error.title',
              defaultMessage: 'Оплата не прошла'
            }),
            subTitle: formatMessage({
              id: 'payment.error.text',
              defaultMessage:
                'Повторите попытку или попробуйте сделать это другим способом'
            })
          }}
        />
      )}

      <div
        className={clsx(
          'mb-4 flex flex-col gap-4 tablet:mb-5 tablet:flex-row-reverse',
          'tablet:items-center tablet:justify-between desktop:mb-6'
        )}
      >
        <SortSelectBox
          buttonClassName="!w-fit"
          {...sortParams}
          options={SORT_OPTIONS}
          placeholder={formatMessage({
            id: 'sort',
            defaultMessage: 'Сортировать'
          })}
        />
        <div className="flex flex-wrap gap-2 tablet:gap-3">
          <SelectBox
            buttonClassName="border-[0.5px] !border-slate-300 !rounded-lg min-w-[156px] h-8"
            hasResetButton={preorder !== null}
            onChange={({ target: { value } }) => setPreorder(value)}
            onReset={() => setPreorder(DEFAULT_PREORDER)}
            options={AVAILABILITY_OPTIONS}
            value={preorder}
            placeholder={formatMessage({
              id: 'shop.filter.availability.all',
              defaultMessage: 'Все устройства'
            })}
          />
          <SelectBox
            buttonClassName="border-[0.5px] !border-slate-300 !rounded-lg min-w-[180px] h-8"
            hasResetButton={locationId !== null}
            onChange={({ target: { value } }) => setLocationId(value)}
            onReset={() => setLocationId(DEFAULT_LOCATION_ID)}
            options={modifiedLocations}
            value={locationId}
            placeholder={formatMessage({
              id: 'shop.filter.location',
              defaultMessage: 'Место установки'
            })}
          />
          {!areSamePrices && (
            <ShopPriceFilterModal
              {...{ form, maxPrice, maxPricePerTH, minPrice, minPricePerTH }}
            />
          )}
        </div>
        <MultiEmailsDialog
          {...{
            isOpen: needsEmailAddition,
            onClose: onAddEmailDialogClose,
            onOpen: onAddEmailDialogOpen,
            needsEmailConfirmation
          }}
        />
      </div>

      <InfiniteScroll
        {...{ isLoading: isFetching, onBottomScroll: fetchNextPage }}
      >
        <div className="space-y-4 desktop:space-y-6">
          {isFetching && !isFetchingNextPage ? (
            <ShopCardSkeleton className="desktop:space-y-6" />
          ) : (
            data?.pages?.map(({ devices }, index) => (
              <Fragment key={index}>
                {devices?.map((device) => (
                  <ShopCard {...{ device }} key={device.id} />
                ))}
              </Fragment>
            ))
          )}
        </div>
        {!isFetching && data?.pages?.[0]?.total === 0 && (
          <ShopEmptyBoard {...{ form }} />
        )}

        {isFetchingNextPage && <Loader className="mt-10" />}
      </InfiniteScroll>
    </div>
  )
}

export default ShopPage
